export { isTerminalCommitment } from '../../api/shared/commitmentStates.js'
import type { CommitmentState } from '../../api/shared/commitmentStates.js'
import { useCallback } from 'react'
import { ApiError } from '@/lib/apiClient'
import { useApiClient } from '@/hooks/useApiClient'

export const COMMITMENT_ACTION_TYPES = [
  'Escribir correo',
  'Abrir chat Teams',
  'Llamar',
  'Preparar reunión',
  'Hacer seguimiento',
  'Buscar decisor',
  'Enviar presentación',
] as const

export type CommitmentActionType = (typeof COMMITMENT_ACTION_TYPES)[number]

export interface CreateCommitmentInput {
  title: CommitmentActionType
  organizacionId: string
  personaId?: string | null
  iniciativaId?: string | null
  vencimiento: string
  descripcion?: string | null
  responsableId?: string | null
}

export type CommitmentOperationalState = CommitmentState

export interface UpdateCommitmentInput {
  reopen?: boolean
  estado?: CommitmentOperationalState
  vencimiento?: string
  responsableId?: string
}

export interface CommitmentSynchronization {
  status: 'ok' | 'error' | 'pending' | 'deferred_to_copilot' | 'not_requested' | 'uncertain' | 'conflict'
  verified?: boolean
  code?: string
  created?: number
  updatedSales?: number | boolean
  updatedToDo?: number
}

export interface CommitmentMutationResult {
  synchronization: CommitmentSynchronization
}

export function useCommitments(): {
  synchronizeCommitment: (id: string, adoptLegacy?: boolean) => Promise<CommitmentMutationResult>
  createCommitment: (input: CreateCommitmentInput) => Promise<CommitmentMutationResult>
  updateCommitment: (id: string, input: UpdateCommitmentInput) => Promise<CommitmentMutationResult>
} {
  const { makeClient } = useApiClient()

  const createCommitment = useCallback(async (input: CreateCommitmentInput) => {
    const client = makeClient()
    if (!client) throw new ApiError('API_UNAUTHORIZED', 'No hay una sesión activa para guardar el compromiso.')
    const result = await client.postOnce('/api/commitments', input)
    return result as CommitmentMutationResult
  }, [makeClient])

  const updateCommitment = useCallback(async (id: string, input: UpdateCommitmentInput) => {
    const client = makeClient()
    if (!client) throw new ApiError('API_UNAUTHORIZED', 'No hay una sesión activa para actualizar el compromiso.')
    const result = await client.patch(`/api/commitments/${id}`, input)
    return result as CommitmentMutationResult
  }, [makeClient])

  const synchronizeCommitment = useCallback(async (id: string, adoptLegacy = false) => {
    const client = makeClient()
    if (!client) throw new ApiError('API_UNAUTHORIZED','No hay sesión activa.')
    return client.post<CommitmentMutationResult>(`/api/commitments/${id}/synchronize`,{adoptLegacy})
  },[makeClient])
  return { createCommitment, updateCommitment, synchronizeCommitment }
}

export function synchronizationMessage(sync: CommitmentSynchronization): string {
  if (sync.status === 'ok' && sync.verified) return 'Sincronización verificada con Microsoft To Do.'
  if (sync.status === 'not_requested') return 'Guardado en Sales. Usa Sincronizar con To Do para vincularlo.'
  if (sync.status === 'uncertain') return 'Resultado Microsoft incierto. Comprobar de nuevo solo verificará la operación; no creará otra tarea.'
  if (sync.code === 'SYNC_LEGACY_ADOPTION_REQUIRED') return 'La tarea antigua requiere adopción explícita por su responsable.'
  if (sync.code === 'SYNC_OWNER_REQUIRED') return 'Guardado en Sales. Debe sincronizarlo el responsable con su propia cuenta Microsoft.'
  if (sync.status === 'conflict') return `Hay un conflicto de sincronización que requiere revisión${sync.code ? ` (${sync.code})` : ''}.`
  return 'El estado Microsoft no está confirmado. El compromiso permanece guardado en Sales.'
}

export interface CommitmentSyncSummary {
  status: 'not_linked' | 'adoption_required' | 'uncertain' | 'last_verified' | 'pending' | 'invalid'
  verifiedAt?: string | null
}
export function synchronizationLabel(summary?: CommitmentSyncSummary): string {
  if (!summary) return 'Sincronización no comprobada'
  const labels = {not_linked:'Sin vínculo con To Do',adoption_required:'Tarea anterior: adopción pendiente',uncertain:'Resultado incierto: comprobar operación',last_verified:'Sincronización verificada anteriormente',pending:'Cambio pendiente de sincronizar',invalid:'Registro de sincronización inconsistente'}
  return labels[summary.status]
}
